import React, { useState } from 'react';
import '../Properties/property.scss';
import PropTypes from 'prop-types';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PropertyLayout from './layout';
import useApiState from '../../Hooks/useApiState';

function CommercialDetailsForm({ prevStep, onNext, onSave, onBackToDashboard, initialValues = {}, step = 8, totalSteps = 10, systemId, isView = false }) {
  const {
    isSubmitting,
    submitError,
    clearErrors,
    submitData,
  } = useApiState({
    showToastOnError: true,
    defaultErrorMessage: 'Failed to save commercial details',
  });

  const initialState = {
    listingType: '',
    price: null,
  };
  const [form, setForm] = useState({ ...initialState, ...initialValues });

  React.useEffect(() => {
    if (initialValues) {
      setForm((prev) => ({ ...prev, ...initialValues }));
    }
  }, [initialValues]);

  const handleChange = (event) => {
    if (isView) return; // Prevent changes in view mode
    clearErrors();
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value ? value : null }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    clearErrors();

    try {
      await submitData(async () => {
        return form;
      }, {
        successMessage: 'Commercial details saved successfully',
      });

      onNext(form);
    } catch (error) {
      console.error('Failed to save commercial details:', error);
    }
  };
  // Helper to save form data before navigating
  const handlePrev = () => {
    onSave(form); // Save form data only
    prevStep(); // Go to previous step
  };

  return (
    <PropertyLayout step={step} totalSteps={totalSteps} systemId={systemId} onBackToDashboard={onBackToDashboard}>
      <div className='card'>
        <h3 className='card-title'>
          <AttachMoneyIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Commercial Details
        </h3>

        {/* Display API errors */}
        {submitError && (
          <div className="error-message" style={{
            padding: '10px',
            marginBottom: '15px',
            backgroundColor: '#ffebee',
            color: '#c62828',
            borderRadius: '4px',
            border: '1px solid #ffcdd2',
          }}>
            {submitError}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Row 1: Purchase Date and Price */}
          <div className='form-row-two'>
            <div className='form-group'>
              <label htmlFor='purchaseDate'>Purchase Date</label>
              <input
                type='date'
                id='purchaseDate'
                name='purchaseDate'
                value={form.purchaseDate || ''}
                onChange={handleChange}
                className='property-date-input'
                style={{ fontSize: 15, padding: '8px 12px', border: '1px solid #ccc', borderRadius: 6, minHeight: 38 }}
                disabled={isSubmitting || isView}
                readOnly={isView}
              />
            </div>
            <div className='form-group'>
              <label htmlFor='price'>Purchase Price (₹)</label>
              <input
                type='number'
                step='0.01'
                id='price'
                name='price'
                placeholder='e.g., 35,00,000'
                value={form.price || ''}
                onChange={handleChange}
                style={{ fontSize: 15, padding: '8px 12px', border: '1px solid #ccc', borderRadius: 6, minHeight: 38 }}
                disabled={isSubmitting || isView}
                readOnly={isView}
              />
            </div>
          </div>
          {/* Row 2: Estimated Current Value */}
          <div className='form-row-two'>
            <div className='form-group'>
              <label htmlFor='estimatedValue'>Estimated Current Value (₹)</label>
              <input
                type='number'
                step='0.01'
                id='estimatedValue'
                name='estimatedValue'
                placeholder='e.g., 35,00,000'
                value={form.estimatedValue || ''}
                onChange={handleChange}
                style={{ fontSize: 16, padding: '8px 12px' }}
                disabled={isSubmitting || isView}
                readOnly={isView}
              />
              <div className='field-note' style={{ fontSize: 13, color: '#888', marginTop: 4 }}>
                Provide your estimated current market value of the property
              </div>
            </div>
          </div>
          {/* Actions */}
          <div className='form-actions'>
            <button type='button' className='btn secondary' onClick={handlePrev} disabled={isSubmitting}>
              Previous
            </button>
            {isView ? (
              <button
                type='button'
                className='btn primary'
                onClick={() => onNext(form)}
              >
                Next
              </button>
            ) : (
              <button type='submit' className='btn primary' disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Next'}
              </button>
            )}
          </div>
        </form>
      </div>
    </PropertyLayout>
  );
}

CommercialDetailsForm.propTypes = {
  prevStep: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onBackToDashboard: PropTypes.func,
  initialValues: PropTypes.object,
  step: PropTypes.number,
  totalSteps: PropTypes.number,
  systemId: PropTypes.string,
  isView: PropTypes.bool,
};

export default CommercialDetailsForm;
